/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { LogLevel } from "@azure/msal-browser";
import * as opt from "lib/opt";
import * as util from "lib/util";

// Update the envKey type to include your new variables
type envKey =
	| "BASE_API_URL"
	| "AI_SEARCH_API_URL"
	| "AI_SEARCH_ENABLED"
	| "CLIENT_ID"
	| "AUTHORITY"
	| "CNTNMT_HLD_INITIAL_ENABLED"
	| "CNTNMT_RELEASE_ENABLED"
	| "API_DEFAULT_TIMEOUT_MS"
	| "API_UPLOAD_TIMEOUT_MS"
	| "API_DOWNLOAD_TIMEOUT_MS";
type prependReactApp<T extends string> = `REACT_APP_${T}`;
type envVarName = prependReactApp<envKey>;
type vars = Record<envVarName, string | undefined>;
type processedVars = Record<envKey, opt.Opt<string>>;

class dotEnv {
	private processedVars: processedVars = this.processVars();

	private processVars(): processedVars {
		const rawVars = this.getRawVars();
		const result: processedVars = {} as processedVars;

		// Process REACT_APP_ prefixed variables
		for (const key of Object.keys(rawVars)) {
			if (key.startsWith("REACT_APP_")) {
				const envKey = key.replace("REACT_APP_", "") as envKey;
				if (envKey) {
					result[envKey] = opt.FromNullable(rawVars[key as envVarName]);
				}
			}
		}

		return result;
	}

	private validateVars(vars: any): vars is vars {
		return typeof vars === "object" && !!Object.keys(vars).length;
	}

	get AuthConfig(): AuthConfig {
		return {
			clientId: this.Get("CLIENT_ID"),
			logLevel: LogLevel.Info,
			authority: this.Get("AUTHORITY"),
			redirectUri: window.location.origin,
			responseType: "code",
			scope: "openid profile email offline_access",
			showDebugInformation: true,
			// turn off validation that discovery document endpoints start with the issuer url defined above
			strictDiscoveryDocumentValidation: false,
			preserveRequestedRoute: true,
			postLogoutRedirectUri: window.location.origin,
			/* eslint-disable */
			post_logout_redirect_uri: window.location.origin,
			logoutUrl: window.location.origin,
		};
	}

	private getRawVars(): vars {
		const out =
			typeof window !== "undefined" && window._env_
				? window._env_
				: import.meta.env;

		if (this.validateVars(out)) {
			return out as vars;
		}
		
		// Provide more helpful error message
		const envKeys = Object.keys(out || {}).filter(key => key.startsWith("REACT_APP_"));
		console.error("Environment variables error:", {
			hasWindowEnv: typeof window !== "undefined" && !!window._env_,
			importMetaEnvKeys: Object.keys(import.meta.env || {}),
			reactAppKeys: envKeys,
			allKeys: Object.keys(out || {})
		});
		log.Fatal(
			"Environment variables not set correctly. " +
			"Make sure you have a .env.local file with REACT_APP_ prefixed variables. " +
			`Found ${envKeys.length} REACT_APP_ variables.`
		);
	}

	Get(key: envKey): string {
		return this.processedVars[key].Expect(
			`Environment variable ${key} not set`
		);
	}
}

const dotEnvInstance = new dotEnv();

export const { Get, AuthConfig } = util.BindMethods(dotEnvInstance);

interface AuthConfig {
	clientId: string;
	logLevel: LogLevel;
	authority: string;
	redirectUri: string;
	responseType: string;
	scope: string;
	showDebugInformation: bool;
	strictDiscoveryDocumentValidation: bool;
	preserveRequestedRoute: bool;
	postLogoutRedirectUri: string;
	post_logout_redirect_uri: string;
	logoutUrl: string;
}
